import { useTranslation } from "react-i18next";
import { i18n, Icon } from "@thetreep/ui";
import { useState } from "react";
import { getColor } from "@utils/colors";
import { DAP } from "@gen/dap/v1/dap_pb";
import {
  Transportation,
  TrainCabinClass,
  PlaneCabinClass,
} from "@gen/dap/v1/dap_pb";
import { getEnumName } from "@utils/enums";
import { getTransportationSrc } from "@utils/transportations";

export function Conditions({
  dap,
  kind,
}: {
  dap?: DAP;
  kind?: "booking" | "home";
}) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const tauxPec = dap?.tauxPec;
  const journeysBooked = dap?.journeysBooked;
  const maxJourneys = dap?.maxJourneys;

  const transportList = dap?.transportations
    .map((transportation) => getEnumName(Transportation, transportation))
    .filter((name): name is string => Boolean(name)); // Remove undefined values

  const getCabinClass = (transportation: string) => {
    switch (transportation) {
      case "TRAIN":
        return t(
          `booking.conditions.${getEnumName(
            TrainCabinClass,
            dap?.trainCabinClass ?? 0,
          )?.toLowerCase()}`,
        );
      case "PLANE":
        return t(
          `booking.conditions.${getEnumName(
            PlaneCabinClass,
            dap?.planeCabinClass ?? 0,
          )?.toLowerCase()}`,
        );
      default:
        return null;
    }
  };

  const getMaxFare = (transportation: string) => {
    if (transportation === "TRAIN" && dap?.trainMaxPriceInCents) {
      return t("booking.conditions.maxFare", {
        amount: new Intl.NumberFormat(i18n.language, {
          style: "currency",
          currency: "EUR",
          maximumFractionDigits: 0,
        }).format(Number(dap?.trainMaxPriceInCents) / 100),
      });
    }

    if (transportation === "PLANE" && dap?.planeMaxPriceInCents) {
      return t("booking.conditions.maxFare", {
        amount: new Intl.NumberFormat(i18n.language, {
          style: "currency",
          currency: "EUR",
          maximumFractionDigits: 0,
        }).format(Number(dap?.planeMaxPriceInCents) / 100),
      });
    }

    return null;
  };

  return (
    <div
      className={`conditions fixed z-[100] top-[173px] md:top-[233px] left-0 right-0 lg:sticky lg:top-[283px] rounded-lg bg-white lg:mx-0 lg:p-6 lg:self-start min-w-[240px] lg:w-[240px] shadow-[0px_0px_48px_0px_#00000029] lg:shadow-none ${
        kind ? `${kind}` : ""
      }`}
    >
      <h2
        onClick={() => setIsOpen(!isOpen)}
        className="title pointer  text-cnam-blue font-jakarta text-[16px] flex justify-between items-center py-4 px-6 border-b border-[#D0D9E9] 
       lg:pointer-events-none  lg:text-[24px] lg:pt-0 lg:px-0"
      >
        {t("booking.conditions.title")}
        <div className={`lg:hidden ${isOpen ? "block rotate-180" : ""}`}>
          <Icon src="down_line" size={24} />
        </div>
      </h2>

      <div
        className={`p-4  ${
          isOpen ? "block" : "hidden"
        } list-wrapper bg-white absolute w-full lg:relative lg:block lg:p-0 lg:mt-4 z-[50] border-b border-[#D0D9E9] lg:border-none`}
      >
        {dap ? (
          <ul className="list flex flex-col gap-6">
            {tauxPec && tauxPec > 0 && (
              <li>
                <div className="flex gap-2">
                  <Icon src="currency" size={24} color={"#3079F2"} />

                  <div>
                    <p
                      className="text-strong font-bold text-md"
                      data-testid="dap-conditions-tauxpec"
                    >
                      {t("booking.conditions.coverage", {
                        percent: tauxPec === 1 ? "55" : "100",
                      })}
                    </p>

                    <div className="flex flex-col mt-1"></div>
                  </div>
                </div>
              </li>
            )}

            {maxJourneys !== undefined && journeysBooked !== undefined && (
              <li>
                <div className="flex gap-2">
                  <Icon src="travel" size={24} color={"#3079F2"} />

                  <div>
                    <span className="text-strong font-bold text-base">
                      {" "}
                      {t("booking.conditions.remainingTravels")}
                    </span>

                    <div className="flex flex-col mt-1">
                      <p
                        className="text-base text-strong flex gap-2 items-center"
                        data-testid="dap-conditions-remainingjourneys"
                      >
                        {t("booking.conditions.remainingTravelsValue", {
                          remaining: maxJourneys - journeysBooked,
                          total: maxJourneys,
                        })}
                      </p>
                    </div>
                  </div>
                </div>
              </li>
            )}
            {transportList?.map((transportation) => {
              const translatedLabel = t(
                `booking.conditions.${transportation.toLocaleLowerCase()}`,
              );

              const maxFare = getMaxFare(transportation);

              return (
                <li key={transportation}>
                  <div className="flex gap-2">
                    <Icon
                      src={getTransportationSrc(transportation)}
                      size={24}
                      color={"#3079F2"}
                    />

                    <div>
                      <span className="text-strong font-bold text-base capitalize">
                        {translatedLabel}
                      </span>

                      <div className="flex flex-col mt-1">
                        {(transportation === "TRAIN" ||
                          transportation === "PLANE") && (
                          <div className="text-base text-strong flex gap-2 items-center">
                            <Icon
                              src="check_line"
                              size={16}
                              color={getColor("text-strong")}
                            />
                            <span
                              data-testid={`dap-conditions-class-${transportation.toLowerCase()}`}
                            >
                              {getCabinClass(transportation)}
                            </span>
                          </div>
                        )}

                        {maxFare && (
                          <div className="text-base text-strong flex gap-2 items-center">
                            <Icon
                              src="check_line"
                              size={16}
                              color={getColor("text-strong")}
                            />
                            <span>{maxFare}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </li>
              );
            })}
            {!!dap.companions && dap.companions > 0 && (
              <li key="companons">
                <div className="flex gap-2">
                  <Icon src="group_line" size={24} color={"#3079F2"} />

                  <div>
                    <span className="text-strong font-bold text-base">
                      {t("booking.conditions.companionNumber")}
                    </span>

                    <div
                      className="flex flex-col mt-1"
                      data-testid="dap-conditions-companions"
                    >
                      {`${dap.companions} ${t("booking.conditions.maximum")}`}
                    </div>
                  </div>
                </div>
              </li>
            )}
          </ul>
        ) : (
          <p className="text-neutral-600 text-xs lg:text-sm">
            {t("booking.conditions.placeholder")}
          </p>
        )}
      </div>
    </div>
  );
}
